import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Paper,
  Box,
  Avatar,
  Grid,
  Button,
  alpha,
  useTheme,
  CircularProgress
} from '@mui/material';
import {
  Group as GroupIcon,
  Event as EventIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  ArrowForward as ArrowForwardIcon,
  WavingHand as WaveIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useGroups } from '../contexts/GroupContext';
import UserInvitations from '../components/UserInvitations';
import PageContainer from '../components/PageContainer';

export default function DashboardPage() {
  const { currentUser, userProfile, loading } = useAuth();
  const { groups } = useGroups();
  const navigate = useNavigate();
  const theme = useTheme();

  const userData = useMemo(() => {
    if (!currentUser) return null;
    return {
      ...currentUser,
      ...userProfile,
      displayName: currentUser.displayName || userProfile?.displayName || 'User',
      email: currentUser.email || userProfile?.email || 'No email',
      photoURL: currentUser.photoURL || userProfile?.photoURL,
      parties: userProfile?.parties || [],
    };
  }, [currentUser, userProfile]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  if (loading) {
    return (
      <PageContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (!userData) return null;

  const StatCard = ({ icon, count, label, color, onClick }) => (
    <Paper
      onClick={onClick}
      sx={{
        p: 3,
        height: '100%',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          backgroundColor: alpha(color, 0.1),
          borderColor: alpha(color, 0.3)
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: '4px',
          backgroundColor: color
        }
      }}
    >
      <Box
        sx={{
          p: 2,
          borderRadius: '50%',
          backgroundColor: alpha(color, 0.1),
          color: color,
          mb: 2,
          boxShadow: `0 0 20px ${alpha(color, 0.2)}`
        }}
      >
        {icon}
      </Box>
      <Typography variant="h3" fontWeight="bold" sx={{ color: 'text.primary', mb: 0.5 }}>
        {count}
      </Typography>
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        {label}
      </Typography>
    </Paper>
  );

  const ActionButton = ({ icon, label, onClick, color }) => (
    <Button
      variant="outlined"
      fullWidth
      startIcon={icon}
      onClick={onClick}
      sx={{
        py: 2,
        justifyContent: 'flex-start',
        px: 3,
        borderColor: alpha(color, 0.3),
        color: 'text.primary',
        backgroundColor: alpha(color, 0.05),
        '&:hover': {
          backgroundColor: alpha(color, 0.1),
          borderColor: color,
          transform: 'translateX(5px)'
        },
        transition: 'all 0.2s ease'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
        <Typography variant="body1" fontWeight={600}>{label}</Typography>
        <ArrowForwardIcon fontSize="small" sx={{ opacity: 0.5 }} />
      </Box>
    </Button>
  );

  return (
    <PageContainer>
      {/* Header Section */}
      <Box sx={{ mb: 6, display: 'flex', alignItems: 'center', gap: 3 }}>
        <Avatar
          src={userData.photoURL}
          alt={userData.displayName}
          sx={{
            width: 80,
            height: 80,
            border: `3px solid ${theme.palette.primary.main}`,
            boxShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.4)}`
          }}
        />
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="h4" component="h1" fontWeight="bold">
              {getGreeting()}, {userData.displayName.split(' ')[0]}
            </Typography>
            <WaveIcon sx={{ color: 'warning.main', animation: 'wave 2s infinite' }} />
          </Box>
          <Typography variant="body1" color="text.secondary">
            Ready to plan your next big event?
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* Stats Row */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2, px: 1 }}>Overview</Typography>
          <Grid container spacing={3}>
            <Grid item xs={6} md={3}>
              <StatCard
                icon={<GroupIcon fontSize="large" />}
                count={groups.length}
                label="Active Groups"
                color={theme.palette.primary.main}
                onClick={() => navigate('/groups')}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                icon={<EventIcon fontSize="large" />}
                count={userData.parties.length}
                label="Parties Planned"
                color={theme.palette.secondary.main}
                onClick={() => navigate('/parties')}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                icon={<CalendarIcon fontSize="large" />}
                count="0" // Placeholder for upcoming events count
                label="Upcoming Events"
                color={theme.palette.success.main}
                onClick={() => navigate('/calendar')}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                icon={<PersonIcon fontSize="large" />}
                count="100%"
                label="Profile Complete"
                color={theme.palette.info.main}
                onClick={() => navigate('/profile')}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Main Content Area */}
        <Grid item xs={12} md={8}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2, px: 1 }}>Recent Invitations</Typography>
          <UserInvitations />
        </Grid>

        {/* Quick Actions Sidebar */}
        <Grid item xs={12} md={4}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2, px: 1 }}>Quick Actions</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <ActionButton
              icon={<GroupIcon sx={{ color: theme.palette.primary.main }} />}
              label={groups.length > 0 ? 'Manage Groups' : 'Create Group'}
              onClick={() => navigate('/groups')}
              color={theme.palette.primary.main}
            />
            <ActionButton
              icon={<EventIcon sx={{ color: theme.palette.secondary.main }} />}
              label="Plan a Party"
              onClick={() => navigate('/parties')}
              color={theme.palette.secondary.main}
            />
            <ActionButton
              icon={<CalendarIcon sx={{ color: theme.palette.success.main }} />}
              label="Check Availability"
              onClick={() => navigate('/calendar')}
              color={theme.palette.success.main}
            />
            <ActionButton
              icon={<PersonIcon sx={{ color: theme.palette.info.main }} />}
              label="Update Profile"
              onClick={() => navigate('/profile')}
              color={theme.palette.info.main}
            />
          </Box>
        </Grid>
      </Grid>
    </PageContainer>
  );
}
